import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Copy, HandHeart, Pencil, Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { getJSON } from '@/lib/api'
import { CampaignFormDialog, type AdminCampaign } from '@/pages/AdminCampaignsPage'
import { useViewOnly } from '@/components/templates/app-shell'

interface CampaignDetail extends AdminCampaign {
  raised: string
  progress_percent: number | null
  gift_count: number
  donor_count: number
  public_url: string
}

export function AdminCampaignDetailPage({
  campaign,
  onBack,
}: {
  campaign: AdminCampaign
  onBack: () => void
}) {
  const viewOnly = useViewOnly()
  const [editOpen, setEditOpen] = React.useState(false)
  const query = useQuery({
    queryKey: ['admin-campaign', campaign.id],
    queryFn: () => getJSON<{ campaign: CampaignDetail }>(`/api/v1/campaigns/${campaign.id}`),
  })
  const detail = query.data?.campaign
  if (query.isLoading)
    return <p className="p-6 text-body-sm text-muted-foreground">Loading campaign…</p>
  if (query.isError || !detail)
    return <p className="p-6 text-body-sm text-destructive">Campaign unavailable.</p>
  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="mb-3 -ml-2 text-muted-foreground"
        onClick={onBack}
      >
        <ArrowLeft className="mr-2 h-4 w-4" /> Campaigns
      </Button>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-subheading font-semibold">{detail.name}</h1>
            <Badge variant="success">Active</Badge>
          </div>
          <p className="mt-1 text-caption text-muted-foreground">
            {detail.open_on
              ? `Open ${detail.open_on} · closes ${detail.close_on}`
              : 'No campaign window'}
          </p>
        </div>
        {!viewOnly && (
          <Button type="button" variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Edit
          </Button>
        )}
      </header>
      <div className="mt-5 grid gap-3 md:grid-cols-3">
        <Metric icon={HandHeart} label="Raised" value={detail.raised}>
          {detail.goal && detail.progress_percent !== null && (
            <>
              <p className="text-caption text-muted-foreground">
                of {detail.goal} goal · {detail.progress_percent}%
              </p>
              <Progress value={Math.min(detail.progress_percent, 100)} className="mt-2" />
            </>
          )}
        </Metric>
        <Metric icon={HandHeart} label="Gifts" value={String(detail.gift_count)} />
        <Metric icon={Users} label="Donors" value={String(detail.donor_count)} />
      </div>
      <section className="mt-4 flex items-center gap-4 rounded-md border border-border bg-card px-4 py-3">
        <span className="text-body-sm font-medium">Public Campaign page</span>
        <a
          className="min-w-0 flex-1 truncate text-caption text-muted-foreground hover:text-foreground"
          href={detail.public_url}
        >
          {window.location.origin}
          {detail.public_url}
        </a>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-8 px-0"
          aria-label="Copy public Campaign page URL"
          onClick={() =>
            navigator.clipboard?.writeText(`${window.location.origin}${detail.public_url}`)
          }
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </section>
      <section className="mt-4 rounded-md border border-border bg-card px-4 py-3">
        <h2 className="text-body-sm font-medium">Campaign details</h2>
        <dl className="mt-3 grid gap-3 text-caption sm:grid-cols-2">
          <div>
            <dt className="text-muted-foreground">Goal</dt>
            <dd className="mt-1 text-body-sm">{detail.goal ?? 'none'}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Window</dt>
            <dd className="mt-1 text-body-sm">
              {detail.open_on ? `${detail.open_on} – ${detail.close_on}` : 'none'}
            </dd>
          </div>
        </dl>
      </section>
      {!viewOnly && (
        <CampaignFormDialog
          campaign={detail}
          open={editOpen}
          onOpenChange={setEditOpen}
          onSaved={() => query.refetch()}
        />
      )}
    </div>
  )
}

function Metric({
  icon: Icon,
  label,
  value,
  children,
}: {
  icon: typeof HandHeart
  label: string
  value: string
  children?: React.ReactNode
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded bg-primary/10 text-primary">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <span className="text-caption text-muted-foreground">{label}</span>
        </div>
        <p className="mt-3 text-subheading font-semibold tabular-nums">{value}</p>
        {children}
      </CardContent>
    </Card>
  )
}
